/**
 * The wallet's dashboard summary (lifetime $TRUTH, cashback, pool share, tier).
 *
 * Refetches on two triggers, both inherited from the Next.js client:
 *   - the wallet changes;
 *   - a license purchase succeeds — and then only after a 3.5 minute delay,
 *     because the figures are recomputed by the syncer job, so reading sooner
 *     returns the pre-purchase snapshot and the dashboard appears not to have
 *     registered the sale.
 */
import { logger } from "@swarm/shared/logger";
import type { RewardsInfoData } from "@swarm/shared/schemas";
import { useEffect, useState } from "react";

import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { getRewardsInfo } from "@/lib/api/rewards";

/** How long after a purchase the syncer has caught up. See the file header. */
const POST_PURCHASE_REFETCH_DELAY_MS = 210_000;

export const useRewardsInfo = (wallet?: string) => {
  const [data, setData] = useState<RewardsInfoData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { transactionOutput } = useSuiQueryContext();

  const fetchRewards = async () => {
    if (!wallet) return;

    setIsLoading(true);

    try {
      // Was https://agent-reward-info.swarmnetwork.ai (the get-rewards-info
      // Cloudflare Worker), which still reads the frozen Neon database and so
      // served numbers that stopped advancing at the Railway cutover. Same
      // response shape, now same-origin.
      const json = await getRewardsInfo({ wallet });

      if (!json.success) {
        throw new Error(json.error);
      }

      setData(json.data);
    } catch (err) {
      logger.error(
        { wallet, err: err instanceof Error ? { message: err.message } : err },
        "failed to fetch rewards info",
      );
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!wallet) return;

    const timer = setTimeout(fetchRewards, POST_PURCHASE_REFETCH_DELAY_MS);

    // Cleared on unmount: without this a wallet switch leaves the old timer
    // running and it overwrites the new wallet's numbers minutes later.
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on a purchase, not on the identity of `fetchRewards`
  }, [wallet, transactionOutput.purchaseLicense.onSuccess]);

  useEffect(() => {
    if (!wallet) return;

    fetchRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the wallet only
  }, [wallet]);

  return { data, isLoading, error, refetch: fetchRewards };
};
